// ─── Utilidades financieras del módulo de Caja (Chile / CLP / IVA 19%) ───────

export const IVA_RATE = 0.19;

// Paleta del sistema (reutilizada en el módulo financiero)
export const FINANCE_COLORS = {
  PRIMARY: '#1A3A5C',
  TEAL: '#2CB8A0',
  AMBER: '#F59E0B',
  DANGER: '#DC2626',
  SUCCESS: '#10B981',
};

// Medios de pago soportados
export const MEDIOS_PAGO = [
  { id: 'efectivo', label: 'Efectivo' },
  { id: 'debito', label: 'Débito' },
  { id: 'credito', label: 'Crédito' },
  { id: 'transferencia', label: 'Transferencia' },
  { id: 'webpay', label: 'WebPay' },
  { id: 'convenio', label: 'Convenio' },
  { id: 'otro', label: 'Otro' },
];

// Tipos de línea del ticket
export const TIPOS_ITEM = [
  'consulta',
  'laboratorio',
  'cirugia',
  'hospitalizacion',
  'farmacia',
  'otro',
];

// Formatea un número a CLP sin decimales: $12.345
export const CLP = (n) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Math.round(Number(n) || 0));

// Formato de fecha corta chilena: 07/07/2026
export const fmtFecha = (v) => {
  if (!v) return '–';
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  if (isNaN(d)) return '–';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Formato fecha + hora: 07/07/2026 14:30
export const fmtFechaHora = (v) => {
  if (!v) return '–';
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  if (isNaN(d)) return '–';
  return d.toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// Fecha de hoy en formato YYYY-MM-DD (para fechaTurno)
export const hoyISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// El precio de los aranceles INCLUYE IVA → neto = precio / 1.19
export const netoDesdeBruto = (bruto) => Math.round((Number(bruto) || 0) / (1 + IVA_RATE));
export const ivaDesdeBruto = (bruto) => (Math.round(Number(bruto) || 0) - netoDesdeBruto(bruto));

// Aplica un descuento porcentual a un monto bruto y devuelve el bruto con descuento
export const aplicarDescuentoPct = (bruto, pct) => {
  const b = Number(bruto) || 0;
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return Math.round(b * (1 - p / 100));
};

// Devuelve el porcentaje de descuento de un convenio para un tipo/arancel dado.
// Las excepciones por arancelId tienen prioridad sobre la regla general del tipo de prestación.
export const getConvenioDiscount = (convenio, tipoPrestacionName, arancelId) => {
  if (!convenio) return 0;
  const exceptions = convenio.discountExceptions || {};
  if (arancelId != null && exceptions[arancelId] != null) {
    return Number(exceptions[arancelId]) || 0;
  }
  const byTipo = convenio.discountByTipoPrestacion || {};
  return Number(byTipo[tipoPrestacionName] || 0);
};

// Devuelve el convenio activo que corresponde al nombre guardado en el tutor.
export const findConvenioByName = (convenios, name) => {
  const raw = (name || '').toString().trim();
  if (!raw) return null;
  const norm = raw.toLowerCase();
  return (convenios || []).find((c) => {
    if (c.isActive === false) return false;
    if (c.id && c.id.toString().trim().toLowerCase() === norm) return true;
    const cName = (c.name || '').toString().trim();
    if (!cName) return false;
    return cName.toLowerCase() === norm;
  }) || null;
};

// Convierte un número entero de CLP a palabras (para la boleta)
const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
const DIEZ_DIECINUEVE = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
const DECENAS = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

function menorMil(n) {
  if (n === 0) return '';
  if (n === 100) return 'cien';
  let out = '';
  const c = Math.floor(n / 100);
  const resto = n % 100;
  if (c > 0) out += CENTENAS[c] + ' ';
  if (resto >= 10 && resto < 20) {
    out += DIEZ_DIECINUEVE[resto - 10];
  } else if (resto === 20) {
    out += 'veinte';
  } else if (resto > 20 && resto < 30) {
    out += 'veinti' + UNIDADES[resto - 20];
  } else {
    const d = Math.floor(resto / 10);
    const u = resto % 10;
    if (d > 0) out += DECENAS[d];
    if (d > 0 && u > 0) out += ' y ';
    if (u > 0) out += UNIDADES[u];
  }
  return out.trim();
}

export const numeroAPalabras = (num) => {
  let n = Math.round(Number(num) || 0);
  if (n === 0) return 'cero pesos';
  let out = '';
  const millones = Math.floor(n / 1000000);
  n = n % 1000000;
  const miles = Math.floor(n / 1000);
  const resto = n % 1000;
  if (millones > 0) {
    out += (millones === 1 ? 'un millón' : `${menorMil(millones)} millones`) + ' ';
  }
  if (miles > 0) {
    out += (miles === 1 ? 'mil' : `${menorMil(miles)} mil`) + ' ';
  }
  if (resto > 0) {
    out += menorMil(resto);
  }
  return `${out.trim()} pesos`.replace(/\s+/g, ' ');
};

// Etiqueta legible del medio de pago
export const labelMedio = (id) => (MEDIOS_PAGO.find((m) => m.id === id)?.label || id || '–');

// ─── Helpers de aranceles multi-sucursal ───────────────────────────────────

export const getArancelPrice = (arancel, branchId) => {
  if (!arancel) return 0;
  const prices = arancel.branchPrices;
  if (Array.isArray(prices)) {
    const entry = prices.find((b) => b.branchId === branchId);
    if (entry) return Number(entry.price) || 0;
  }
  // compatibilidad hacia atrás
  if (arancel.branchId && arancel.branchId !== branchId) return 0;
  return Number(arancel.precio) || 0;
};

export const getArancelBranches = (arancel) => {
  if (!arancel) return [];
  if (Array.isArray(arancel.branchPrices)) {
    return arancel.branchPrices.map((b) => b.branchId).filter(Boolean);
  }
  return arancel.branchId ? [arancel.branchId] : [];
};

export const getArancelDisplayPrice = (arancel) => {
  if (!arancel) return 0;
  if (Array.isArray(arancel.branchPrices) && arancel.branchPrices.length) {
    return Number(arancel.branchPrices[0].price) || 0;
  }
  return Number(arancel.precio) || 0;
};
